import React from "react";

import { useHistory } from "react-router-dom";

import {
    ArrowBackIcon,
    Banner,
    Button,
    IconButton,
    NotificationSnackbar,
    ConfirmDialog,
    Skeleton,
    Stack,
} from "shared/ui";

import { ApiKeyStep } from "features/IikoIntegration/components/ApiKeyStep";
import { CatalogSourceStep } from "features/IikoIntegration/components/CatalogSourceStep";
import { CompletedStepRow } from "features/IikoIntegration/components/CompletedStepRow";
import { ConnectionStatusCard } from "features/IikoIntegration/components/ConnectionStatusCard";
import { ConnectionStepper } from "features/IikoIntegration/components/ConnectionStepper";
import { OrganizationStep } from "features/IikoIntegration/components/OrganizationStep";
import { SyncStep } from "features/IikoIntegration/components/SyncStep";
import { TerminalsStep } from "features/IikoIntegration/components/TerminalsStep";
import { UpcomingStepCard } from "features/IikoIntegration/components/UpcomingStepCard";
import { WebhookCard } from "features/IikoIntegration/components/WebhookCard";
import { IikoIntegrationContext } from "features/IikoIntegration/context";
import { useIikoIntegrationState } from "features/IikoIntegration/hooks/useIikoIntegrationState";
import { maskApiKey } from "features/IikoIntegration/lib/apiKeyMask";

import { Container, ContentHeader, ContentHeaderCaption, Muted } from "./styles";

const MENUS_LIST_PATH = "/menus/regular/list";

const IikoIntegrationScreen = () => {
    const state = useIikoIntegrationState();
    const history = useHistory();

    const goBack = () => history.push(MENUS_LIST_PATH);

    if (state.loading) {
        return (
            <Container>
                <Stack>
                    <Skeleton height="36px" width="320px" />
                    <Skeleton height="82px" />
                    <Skeleton height="62px" />
                    <Skeleton height="180px" />
                </Stack>
            </Container>
        );
    }

    if (state.error) {
        return (
            <Container>
                <Banner tone="danger" title="Не удалось загрузить интеграцию">
                    <Button variant="outlined" color="neutral" onClick={state.reload}>
                        Повторить
                    </Button>
                </Banner>
            </Container>
        );
    }

    const caption = state.menuName
        ? `Меню «${state.menuName}» · заказы уходят напрямую в iiko`
        : "Заказы уходят напрямую в iiko";

    // Пройденный шаг сворачивается в строку, текущий раскрыт, будущий — приглушён.
    const isApiKeyDone = state.step > 1 && !state.isApiLoginEditing;
    const isOrganizationDone = state.step > 2 && !state.isOrganizationEditing;

    return (
        <IikoIntegrationContext.Provider value={state}>
            <Container>
                <ContentHeader>
                    <IconButton label="Назад" variant="outlined" size="large" onClick={goBack}>
                        <ArrowBackIcon />
                    </IconButton>
                    <ContentHeaderCaption>{caption}</ContentHeaderCaption>
                </ContentHeader>

                <ConnectionStatusCard />

                {state.showStepper && <ConnectionStepper />}

                {isApiKeyDone ? (
                    <CompletedStepRow
                        title="Ключ Iiko Transport"
                        value={maskApiKey(state.apiLogin ?? "")}
                        mono
                        onEdit={state.startApiLoginEditing}
                    />
                ) : (
                    <ApiKeyStep />
                )}

                {isOrganizationDone && (
                    <CompletedStepRow
                        title="Организация"
                        value={state.organizationName}
                        note="Сохранено автоматически"
                        onEdit={state.startOrganizationEditing}
                    />
                )}

                {!isOrganizationDone &&
                    (state.step >= 2 ? (
                        <OrganizationStep />
                    ) : (
                        <UpcomingStepCard
                            title="Организация"
                            description="Откроется после сохранения ключа — список организаций подтянем из iiko."
                        />
                    ))}

                {state.step >= 3 ? (
                    <CatalogSourceStep />
                ) : (
                    <UpcomingStepCard
                        title="Источник меню"
                        description="Откроется после выбора организации — список внешних меню подтянем из iiko."
                    />
                )}

                {state.step >= 3 ? (
                    <TerminalsStep />
                ) : (
                    <UpcomingStepCard
                        title="Терминалы"
                        description="Откроется после выбора организации — кассы берём из неё."
                    />
                )}

                {state.step >= 3 && <WebhookCard />}

                {state.terminals.length > 0 ? (
                    <SyncStep />
                ) : (
                    <Muted>
                        <SyncStep />
                    </Muted>
                )}
            </Container>

            <ConfirmDialog
                open={state.dialog?.kind === "removeTerminal"}
                title="Удалить терминал?"
                description="Заказы этого меню перестанут уходить на эту кассу."
                confirmLabel="Удалить"
                tone="danger"
                saving={state.saving.terminal}
                onClose={state.closeDialog}
                onConfirm={state.confirmRemoveTerminal}
            />

            <NotificationSnackbar {...state.notify.snackbarProps} />
        </IikoIntegrationContext.Provider>
    );
};

export const IikoIntegration = IikoIntegrationScreen;
